import React, { useEffect } from 'react';

/**
 * Page d'accueil de l'Agent RAG Hybride Banking
 * 
 * INTÉGRATION DANS VOTRE APPLICATION :
 * 
 * 1. Avec React Router :
 * 
 * import { useNavigate } from 'react-router-dom';
 * 
 * const IndexPageWithRouter = () => {
 *   const navigate = useNavigate();
 *   
 *   useEffect(() => {
 *     window.onNavigateToPage = (pageName) => {
 *       navigate(`/ia/produits/agentsuppbancaire/${pageName}`);
 *     };
 *   }, [navigate]);
 *   
 *   return <IndexPage />;
 * };
 * 
 * 2. Avec un gestionnaire de navigation personnalisé :
 * 
 * const MyApp = () => {
 *   const [currentPage, setCurrentPage] = useState('index');
 *   
 *   useEffect(() => {
 *     window.onNavigateToPage = (pageName) => {
 *       setCurrentPage(pageName);
 *     };
 *   }, []);
 *   
 *   return (
 *     <div>
 *       {currentPage === 'index' && <IndexPage />}
 *       {currentPage === 'architecture-overview' && <ArchitectureOverview />}
 *       {currentPage === 'demo-playground' && <DemoPlayground />}
 *       // ... autres pages
 *     </div>
 *   );
 * };
 * 
 * 3. Arborescence des fichiers dans votre projet :
 * pages/IA/produits/agentsuppbancaire/
 *   ├── index-page-react.js (ce fichier)
 *   ├── architecture-overview-react.js
 *   ├── api-documentation-react.js
 *   ├── demo-playground-react.js
 *   ├── deployment-guide-react.js
 *   ├── performance-metrics-react.js
 *   ├── rag-hybrid-deep-dive-react.js
 *   ├── roadmap-phases-react.js
 *   ├── search-flow-process-react.js
 *   ├── security-compliance-react.js
 *   ├── user-profiles-react.js
 *   └── monitoring-analytics-react.js
 */

const IndexPage = () => {
  // Smooth scrolling handler
  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Navigation handler for React components
  const navigateToPage = (e, pageName) => {
    e.preventDefault();
    // Cette fonction sera appelée par le composant parent pour changer de page
    if (window.onNavigateToPage) {
      window.onNavigateToPage(pageName);
    } else {
      console.log(`Navigation vers: ${pageName}`);
      // Fallback: vous pourrez ici intégrer votre logique de navigation
      // Par exemple avec React Router: navigate(`/${pageName}`)
    }
  };

  useEffect(() => {
    // Parallax effect on scroll
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallax = document.querySelector('.hero-bg');
      if (parallax) {
        parallax.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Animate on scroll with Intersection Observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);
    
    // Apply observer to feature cards
    const featureCards = document.querySelectorAll('.feature-card');
    featureCards.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'all 0.6s ease-out';
      observer.observe(el);
    });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            overflow-x: hidden;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.9);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .logo {
            font-size: 1.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .nav-links {
            display: flex;
            gap: 2rem;
            list-style: none;
        }
        
        .nav-links a {
            color: #888;
            text-decoration: none;
            transition: color 0.3s;
        }
        
        .nav-links a:hover {
            color: #fff;
        }
        
        /* Hero Section */
        .hero {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            padding-top: 80px;
        }
        
        .hero-bg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: 
                radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%);
            z-index: -1;
        }
        
        .hero-content {
            text-align: center;
            max-width: 1000px;
            padding: 0 2rem;
        }
        
        .hero h1 {
            font-size: 4rem;
            margin-bottom: 1.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: fadeInUp 1s ease-out;
        }
        
        .hero-subtitle {
            font-size: 1.5rem;
            color: #888;
            margin-bottom: 3rem;
            animation: fadeInUp 1s ease-out 0.2s;
            animation-fill-mode: both;
        }
        
        .hero-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 2rem;
            margin-bottom: 3rem;
            animation: fadeInUp 1s ease-out 0.4s;
            animation-fill-mode: both;
        }
        
        .stat-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 2rem;
            border-radius: 16px;
            backdrop-filter: blur(10px);
            transition: transform 0.3s, background 0.3s;
        }
        
        .stat-card:hover {
            transform: translateY(-5px);
            background: rgba(255, 255, 255, 0.08);
        }
        
        .stat-number {
            font-size: 3rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
            color: #888;
            margin-top: 0.5rem;
        }
        
        .cta-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
            animation: fadeInUp 1s ease-out 0.6s;
            animation-fill-mode: both;
        }
        
        .btn {
            padding: 1rem 2rem;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            transition: all 0.3s;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        .btn-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }
        
        .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .btn-secondary {
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            background: transparent;
        }
        
        .btn-secondary:hover {
            background: rgba(255, 255, 255, 0.1);
        }
        
        /* Features Section */
        .features {
            padding: 5rem 2rem;
            max-width: 1400px;
            margin: 0 auto;
        }
        
        .section-title {
            text-align: center;
            font-size: 3rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .section-subtitle {
            text-align: center;
            color: #888;
            font-size: 1.2rem;
            margin-bottom: 4rem;
        }
        
        .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 2rem;
        }
        
        .feature-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 2rem;
            border-radius: 16px;
            transition: all 0.3s;
            position: relative;
            overflow: hidden;
        }
        
        .feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
            opacity: 0;
            transition: opacity 0.3s;
        }
        
        .feature-card:hover::before {
            opacity: 1;
        }
        
        .feature-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .feature-icon {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            margin-bottom: 1.5rem;
        }
        
        .feature-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            color: #fff;
        }
        
        .feature-description {
            color: #888;
            line-height: 1.6;
        }
        
        .feature-link {
            color: #667eea;
            text-decoration: none;
            margin-top: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 600;
            transition: gap 0.3s;
        }
        
        .feature-link:hover {
            gap: 1rem;
        }
        
        /* Architecture Preview */
        .architecture-preview {
            padding: 5rem 2rem;
            background: rgba(255, 255, 255, 0.02);
            margin: 5rem 0;
        }
        
        .architecture-diagram {
            max-width: 1200px;
            margin: 0 auto;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 3rem;
            position: relative;
            overflow: hidden;
        }
        
        .architecture-flow {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 2rem;
            position: relative;
        }
        
        .arch-component {
            text-align: center;
            padding: 2rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s;
        }
        
        .arch-component:hover {
            transform: scale(1.05);
            background: rgba(102, 126, 234, 0.1);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .arch-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
        }
        
        .arch-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .arch-percentage {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        /* Animated connections */
        .connections {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
        }
        
        .connection-line {
            stroke: #667eea;
            stroke-width: 2;
            fill: none;
            opacity: 0.3;
            stroke-dasharray: 5, 5;
            animation: dash 20s linear infinite;
        }
        
        @keyframes dash {
            to {
                stroke-dashoffset: -100;
            }
        }
        
        /* Footer */
        footer {
            padding: 3rem 2rem;
            text-align: center;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            color: #888;
        }
        
        /* Animations */
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes float {
            0%, 100% {
                transform: translateY(0);
            }
            50% {
                transform: translateY(-10px);
            }
        }
        
        .floating {
            animation: float 3s ease-in-out infinite;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .hero h1 {
                font-size: 2.5rem;
            }
            
            .hero-subtitle {
                font-size: 1.2rem;
            }
            
            .features-grid {
                grid-template-columns: 1fr;
            }
            
            .architecture-flow {
                grid-template-columns: 1fr;
            }
            
            .nav-links {
                display: none;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <div className="logo">🚀 RAG Hybride Banking</div>
          <ul className="nav-links">
            <li><a href="#features" onClick={(e) => handleSmoothScroll(e, '#features')}>Fonctionnalités</a></li>
            <li><a href="#" onClick={(e) => navigateToPage(e, 'architecture-overview')}>Architecture</a></li>
            <li><a href="#" onClick={(e) => navigateToPage(e, 'api-documentation')}>API</a></li>
            <li><a href="#" onClick={(e) => navigateToPage(e, 'deployment-guide')}>Déploiement</a></li>
            <li><a href="#" onClick={(e) => navigateToPage(e, 'demo-playground')}>Demo</a></li>
          </ul>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="hero-content">
          <h1 className="floating">Agent RAG Hybride Banking</h1>
          <p className="hero-subtitle">
            Support L1 Intelligent combinant 4 méthodes de recherche 
            pour une précision maximale dans l'environnement bancaire
          </p>
          
          <div className="hero-stats">
            <div className="stat-card">
              <div className="stat-number">95%</div>
              <div className="stat-label">Précision</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">&lt;2s</div>
              <div className="stat-label">Temps de Réponse</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">4</div>
              <div className="stat-label">Moteurs de Recherche</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Disponibilité</div>
            </div>
          </div>
          
          <div className="cta-buttons">
            <a href="#" onClick={(e) => navigateToPage(e, 'architecture-overview')} className="btn btn-primary">
              Explorer l'Architecture →
            </a>
            <a href="#" onClick={(e) => navigateToPage(e, 'demo-playground')} className="btn btn-secondary">
              Essayer la Demo
            </a>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="features" id="features">
        <h2 className="section-title">Capacités Principales</h2>
        <p className="section-subtitle">Une solution complète pour le support bancaire de niveau 1</p>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🧠</div>
            <h3 className="feature-title">Recherche Vectorielle</h3>
            <p className="feature-description">
              Compréhension sémantique profonde des requêtes grâce aux embeddings 
              et à l'IA pour trouver les informations pertinentes même avec des formulations différentes.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'rag-hybrid-deep-dive')} className="feature-link">
              En savoir plus →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🔍</div>
            <h3 className="feature-title">Recherche Multi-Index</h3>
            <p className="feature-description">
              Combinaison de BM25, recherche vectorielle, graphe de connaissances 
              et métadonnées pour une couverture exhaustive.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'search-flow-process')} className="feature-link">
              Voir le processus →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">👥</div>
            <h3 className="feature-title">Profils Personnalisés</h3>
            <p className="feature-description">
              Réponses adaptées selon le profil : IT, BackOffice, AMOA, MOE. 
              Chaque utilisateur reçoit l'information pertinente pour son rôle.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'user-profiles')} className="feature-link">
              Découvrir les profils →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3 className="feature-title">Performance Optimale</h3>
            <p className="feature-description">
              Cache multi-niveaux, recherche parallèle et infrastructure scalable 
              pour des réponses en moins de 2 secondes.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'performance-metrics')} className="feature-link">
              Voir les métriques →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🔐</div>
            <h3 className="feature-title">Sécurité & Conformité</h3>
            <p className="feature-description">
              Respect des réglementations BEAC/BCEAO, audit trail complet, 
              chiffrement des données et contrôle d'accès granulaire.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'security-compliance')} className="feature-link">
              Détails sécurité →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3 className="feature-title">Analytics Avancées</h3>
            <p className="feature-description">
              Tableaux de bord temps réel, analyses des requêtes, 
              identification des gaps de connaissances et amélioration continue.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'monitoring-analytics')} className="feature-link">
              Explorer analytics →
            </a>
          </div>
        </div>
      </section>
      
      {/* Architecture Preview */}
      <section className="architecture-preview">
        <h2 className="section-title">Architecture RAG Hybride</h2>
        <p className="section-subtitle">4 moteurs de recherche pour une précision maximale</p>
        
        <div className="architecture-diagram">
          <div className="architecture-flow">
            <div className="arch-component">
              <div className="arch-icon">🧠</div>
              <div className="arch-title">Recherche Vectorielle</div>
              <div className="arch-percentage">60%</div>
              <p>Compréhension sémantique</p>
            </div>
            
            <div className="arch-component">
              <div className="arch-icon">🔍</div>
              <div className="arch-title">Keywords BM25</div>
              <div className="arch-percentage">25%</div>
              <p>Correspondance exacte</p>
            </div>
            
            <div className="arch-component">
              <div className="arch-icon">🕸️</div>
              <div className="arch-title">Graphe</div>
              <div className="arch-percentage">10%</div>
              <p>Relations cachées</p>
            </div>
            
            <div className="arch-component">
              <div className="arch-icon">📋</div>
              <div className="arch-title">Métadonnées</div>
              <div className="arch-percentage">5%</div>
              <p>Filtres précis</p>
            </div>
          </div>
          
          <svg className="connections" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path className="connection-line" d="M 12.5 50 Q 37.5 25 62.5 50 T 87.5 50" />
            <path className="connection-line" d="M 12.5 50 Q 37.5 75 62.5 50 T 87.5 50" />
          </svg>
        </div>
        
        <div className="cta-buttons" style={{marginTop: '3rem'}}>
          <a href="#" onClick={(e) => navigateToPage(e, 'architecture-overview')} className="btn btn-primary">
            Explorer l'Architecture Complète →
          </a>
        </div>
      </section>
      
      {/* Quick Links */}
      <section className="features">
        <h2 className="section-title">Accès Rapide</h2>
        <p className="section-subtitle">Naviguez vers les sections importantes</p>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🚀</div>
            <h3 className="feature-title">Quick Start</h3>
            <p className="feature-description">
              Démarrez rapidement avec notre guide d'installation et configuration de base.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'deployment-guide')} className="feature-link">
              Commencer →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📡</div>
            <h3 className="feature-title">API Reference</h3>
            <p className="feature-description">
              Documentation complète de l'API REST avec exemples et cas d'usage.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'api-documentation')} className="feature-link">
              Voir l'API →
            </a>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🗺️</div>
            <h3 className="feature-title">Roadmap</h3>
            <p className="feature-description">
              Découvrez les phases de développement et les fonctionnalités à venir.
            </p>
            <a href="#" onClick={(e) => navigateToPage(e, 'roadmap-phases')} className="feature-link">
              Voir la roadmap →
            </a>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer>
        <p>© 2024 Agent RAG Hybride Banking - Documentation Technique</p>
        <p>Version 1.0 | Dernière mise à jour : Décembre 2024</p>
      </footer>
    </>
  );
};

export default IndexPage;